import { isChurchOwnHost, isSafeNextPath, isValidTenantSlug, normalizeHostname } from "./host";

/** The two apps a church may give its own sign-in address (spec 2026-10-08 section 4, ruling R5). */
export type SignInApp = "staff_dashboard" | "member_portal";

/**
 * public.church_sign_in_addresses: the church's live sign-in hosts (null when an app has none, or it is not live) and
 * "Our preferred address" (R9). The database answers a church's own people only (P6).
 */
export interface ChurchSignInAddresses {
  readonly dash: string | null;
  readonly members: string | null;
  readonly preferred: "how" | "church";
}

/** A church with no live address, and what every link uses when the answer cannot be read (ADR 0067 A6). */
export const NO_SIGN_IN_ADDRESSES: ChurchSignInAddresses = {
  dash: null,
  members: null,
  preferred: "how",
};

/** Where a hand-off goes: the host, and the page there (a plain same-site path). */
export interface AppTarget {
  readonly host: string;
  readonly path: string;
}

/** An "Open at …" item (R10): its words, the address it names (HoW's, shown under the words), and its target. */
export interface AddressSwitch {
  readonly label: string;
  readonly description: string | null;
  readonly target: AppTarget;
}

/** A host name: letter-digit-hyphen labels under a letters-only top-level domain or its punycode form (`xn--…`). */
const HOSTNAME =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9](?:[a-z0-9-]{0,57}[a-z0-9])?)$/;
const FIRST_LABEL: Readonly<Record<SignInApp, string>> = {
  staff_dashboard: "dash.",
  member_portal: "members.",
};

function assertChurch(slug: string): void {
  if (!isValidTenantSlug(slug)) throw new Error("sign-in addresses: malformed church slug");
}

/** The page when it is a plain same-site path (isSafeNextPath, mirroring private.is_safe_next_path); "/" otherwise. */
function pageOrHome(path: string): string {
  return isSafeNextPath(path) ? path : "/";
}

/**
 * The church's own live host for an app, or null. Used only when the database's answer names it, it is a well-formed
 * name, its first label is the app's own (`dash.` / `members.`, R5), and it is outside HoW's domain. The database
 * already guarantees all three (P1, P5); this is defence in depth, so no link can leave for a host it did not name.
 */
export function churchHostFor(
  addresses: ChurchSignInAddresses,
  app: SignInApp,
  baseDomain: string,
): string | null {
  const raw = app === "staff_dashboard" ? addresses.dash : addresses.members;
  if (raw === null) return null;
  const host = normalizeHostname(raw);
  if (!HOSTNAME.test(host) || !host.startsWith(FIRST_LABEL[app])) return null;
  if (!isChurchOwnHost(host, baseDomain)) return null;
  return host;
}

/**
 * HoW's address for a church's app, without a trailing slash (spec section 7): `https://dash.<base>/<slug>` for staff,
 * `https://<slug>.members.<base>` for members. Where emailed links land (R8). Throws for a malformed slug.
 */
export function platformAddressFor(
  church: { readonly slug: string; readonly surface: SignInApp },
  baseDomain: string,
): string {
  assertChurch(church.slug);
  const base = normalizeHostname(baseDomain);
  return church.surface === "staff_dashboard"
    ? `https://dash.${base}/${church.slug}`
    : `https://${church.slug}.members.${base}`;
}

/**
 * HoW's address for a church's app as a hand-off target at `path`. The staff door puts the church first in the path
 * (`/<slug><page>`, ADR 0065); a page that would not be a plain path there opens the church's home. Throws for a
 * malformed slug.
 */
export function howAppTarget(
  slug: string,
  app: SignInApp,
  baseDomain: string,
  path = "/",
): AppTarget {
  assertChurch(slug);
  const base = normalizeHostname(baseDomain);
  const page = pageOrHome(path);
  if (app === "member_portal") return { host: `${slug}.members.${base}`, path: page };
  const onDoor = `/${slug}${page}`;
  return { host: `dash.${base}`, path: isSafeNextPath(onDoor) ? onDoor : `/${slug}/` };
}

/**
 * Where HoW's own links between the apps go (R9): the church's address for the target app when the church prefers its
 * own **and** that app's address is live; otherwise HoW's. The same rule from either address. Throws for a malformed
 * slug (callers run it in a click handler).
 */
export function preferredAppTarget(
  addresses: ChurchSignInAddresses,
  slug: string,
  app: SignInApp,
  baseDomain: string,
  path = "/",
): AppTarget {
  const church =
    addresses.preferred === "church" ? churchHostFor(addresses, app, baseDomain) : null;
  return church === null
    ? howAppTarget(slug, app, baseDomain, path)
    : { host: church, path: pageOrHome(path) };
}

/**
 * The account menu's "Open at …" (R10), the same page on the app's other address, or null when there is none to offer.
 * On the church's own address: always HoW's (the page is live there by definition, A7). On HoW's address: the church's,
 * only while it is live. The preference never changes it. Never throws: it runs during render.
 */
export function addressSwitch(
  addresses: ChurchSignInAddresses,
  slug: string,
  app: SignInApp,
  baseDomain: string,
  onChurchHost: boolean,
  path: string,
): AddressSwitch | null {
  if (!isValidTenantSlug(slug)) return null;
  if (onChurchHost) {
    return {
      label: "Open at HoW's address",
      description: platformAddressFor({ slug, surface: app }, baseDomain).slice("https://".length),
      target: howAppTarget(slug, app, baseDomain, path),
    };
  }
  const church = churchHostFor(addresses, app, baseDomain);
  return church === null
    ? null
    : {
        label: `Open at ${church}`,
        description: null,
        target: { host: church, path: pageOrHome(path) },
      };
}
